import { Modal, ModalTitle, ModalBody, ModalFooter, MODAL_INNER_GAP } from 'design-system/src/components/Modal';
import { Typography } from 'design-system/src/components/Typography/Typography';
import Button from 'design-system/src/components/Button/Button';
import styled from 'styled-components';
import Hairline from 'design-system/src/components/Hairline/Hairline';
import { Drug, OutboundTaskTypeKey } from '../../api';
import { Patient } from '../../api';
import { ExtraInfoTable } from 'design-system/src/components/ExtraInfoTable/ExtraInfoTable';
import { useId, useState } from 'react';
import { Toast, StandardErrorToast } from 'design-system/src/components/Toast/Toast';
import { useMutation } from '@tanstack/react-query';
import { client } from '../../api';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const RadioOption = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
`;

const RadioButton = styled.input`
  cursor: pointer;
  width: 20px;
  height: 20px;
  accent-color: var(--purple-700);
  margin-top: 0;
`;

// putting bodySmallDMSans styles directly here to override the radio label styles
const RadioLabel = styled(Typography)`
  cursor: pointer;
  font-weight: 400;
  font-size: 0.875rem;
`;

export type DeclineReason =
  | 'already_enrolled'
  | 'internal'
  | 'should_not_be_enrolled'
  | 'no_longer_needs'
  // NOTE: this string is used in python/partner_portal/blueprints/rx.py and needs to be kept in sync
  | 'new_therapy'
  | 'patient_unresponsive'
  | 'patient_declined_self_pay'
  | 'pa_completed_externally'
  | 'other';

export type DeclineReasonOption = { label: string; value: DeclineReason };

const reasonOptions: DeclineReasonOption[] = [
  { label: 'The patient is already enrolled', value: 'already_enrolled' },
  { label: 'My practice will manage this enrollment internally', value: 'internal' },
  { label: 'The patient should not be enrolled', value: 'should_not_be_enrolled' },
  { label: 'The patient no longer needs this drug', value: 'no_longer_needs' },
  { label: 'The patient is switching therapies', value: 'new_therapy' },
  { label: 'Patient has been unresponsive to office', value: 'patient_unresponsive' },
  { label: 'Patient has no coverage or insurance and does not wish to self-pay', value: 'patient_declined_self_pay' },
  { label: 'Office completed the PA outside Tandem', value: 'pa_completed_externally' },
  { label: 'Other', value: 'other' },
];

// TODO (ahu): pull out DeclineEnrollmentModal component
const modalEnrollmentDeclineReasons: DeclineReason[] = [
  'already_enrolled',
  'internal',
  'should_not_be_enrolled',
  'other',
];

const modalRenewalDeclineReasons: DeclineReason[] = ['no_longer_needs', 'new_therapy', 'other'];

const modalPrescriptionCancelReasons: DeclineReason[] = ['new_therapy', 'other'];

const modalCashPriceCancelReasons: DeclineReason[] = [
  'patient_unresponsive',
  'new_therapy',
  'patient_declined_self_pay',
  'other',
];

const modalPharmacyRequestedCancelReasons: DeclineReason[] = [
  'patient_unresponsive',
  'new_therapy',
  'pa_completed_externally',
  'other',
];

const modalUnableToVerifyInsuranceCancelReasons: DeclineReason[] = [
  'patient_unresponsive',
  'patient_declined_self_pay',
  'new_therapy',
  'pa_completed_externally',
  'other',
];

// Key type is string since it doesn't account for all task types
const taskKeyToItemLabel: Partial<Record<OutboundTaskTypeKey, string>> = {
  [OutboundTaskTypeKey.DIGITAL_ENROLLMENT_TREMFYA]: 'enrollment',
  [OutboundTaskTypeKey.FORM_SIGNATURE]: 'enrollment',
  [OutboundTaskTypeKey.PA_EXPIRING]: 'renewal',
  [OutboundTaskTypeKey.PA_DENIAL_NEXT_STEPS]: 'appeal',
  [OutboundTaskTypeKey.PA_DENIAL_NEXT_STEPS_DYNAMIC]: 'appeal',
};

type DeclineProps = {
  closeModal: () => void;
  patient: Patient;
  drug: Drug;
  taskKey: OutboundTaskTypeKey;
  taskId: string;
  onTaskUpdate: () => void;
};

const DeclineModal = ({ closeModal, patient, drug, taskKey, taskId, onTaskUpdate }: DeclineProps) => {
  const [declineReason, setDeclineReason] = useState('');
  const navigate = useNavigate();
  const isTrainingMode = import.meta.env.VITE_TRAINING_MODE === 'true';

  const declineTask = useMutation({
    mutationFn: async () => {
      if (isTrainingMode) {
        console.warn(`Training Mode: Mocking "decline task" for task ID: ${taskId}`);
        return;
      }
      await client.post(`tasks/${taskId}/decline`, {
        decline_reason: declineReason,
        item_label: taskKeyToItemLabel[taskKey],
      });
    },
    onSuccess: () => {
      onTaskUpdate();
      toast.custom(() => <Toast variant="success">Task declined</Toast>);
      navigate('/tasks');
    },
    onError: (error) => {
      toast.custom(() => <StandardErrorToast />);
      console.error(error);
      Sentry.captureException(error);
    },
  });

  const cancelPrescription = useMutation({
    mutationFn: async () => {
      if (isTrainingMode) {
        console.warn(`Training Mode: Mocking "cancel prescription" for drug ID: ${drug.id}`);
        return;
      }
      const res = await client.post(`rxs/${drug.id}/cancel`, {
        cancel_reason: declineReason,
      });
      return res.data;
    },
    onSuccess: () => {
      onTaskUpdate();
      toast.custom(() => <Toast variant="success">Prescription cancelled successfully</Toast>);
      navigate('/tasks');
    },
    onError: (error) => {
      toast.custom(() => <StandardErrorToast />);
      console.error(error);
      Sentry.captureException(error);
    },
  });

  const extraInfoData = [
    { label: 'Patient', value: `${patient?.first_name} ${patient?.last_name}` },
    { label: 'Drug', value: drug?.name },
  ];

  const cancelsPrescription = [
    OutboundTaskTypeKey.PA_DENIAL_NEXT_STEPS,
    OutboundTaskTypeKey.PA_DENIAL_NEXT_STEPS_DYNAMIC,
    OutboundTaskTypeKey.PA_INFORMATION_REQUEST,
    OutboundTaskTypeKey.PA_INFORMATION_REQUEST_UNMET_CRITERIA,
    OutboundTaskTypeKey.APPEAL_REVIEW,
    OutboundTaskTypeKey.PA_DENIAL_NEXT_STEPS_AND_APPEAL_REVIEW,
    OutboundTaskTypeKey.NEW_RX_DOCUMENT_UPLOAD,
    OutboundTaskTypeKey.OFFERED_BEST_CASH_PRICE,
    OutboundTaskTypeKey.PHARMACY_REQUESTED,
    OutboundTaskTypeKey.UNABLE_TO_VERIFY_INSURANCE,
  ].includes(taskKey);

  let modalDeclineReasons: DeclineReason[] = [];
  if (taskKey === OutboundTaskTypeKey.OFFERED_BEST_CASH_PRICE) {
    modalDeclineReasons = modalCashPriceCancelReasons;
  } else if (taskKey === OutboundTaskTypeKey.PHARMACY_REQUESTED) {
    modalDeclineReasons = modalPharmacyRequestedCancelReasons;
  } else if (taskKey === OutboundTaskTypeKey.UNABLE_TO_VERIFY_INSURANCE) {
    modalDeclineReasons = modalUnableToVerifyInsuranceCancelReasons;
  } else if (cancelsPrescription) {
    modalDeclineReasons = modalPrescriptionCancelReasons;
  } else {
    switch (taskKey) {
      case OutboundTaskTypeKey.FORM_SIGNATURE:
        modalDeclineReasons = modalEnrollmentDeclineReasons;
        break;
      case OutboundTaskTypeKey.DIGITAL_ENROLLMENT_TREMFYA:
        modalDeclineReasons = modalEnrollmentDeclineReasons;
        break;
      case OutboundTaskTypeKey.PA_EXPIRING:
        modalDeclineReasons = modalRenewalDeclineReasons;
        break;
    }
  }

  const modalDeclineOptions = reasonOptions.filter((option) => modalDeclineReasons.includes(option.value));
  const submitMutation = cancelsPrescription ? cancelPrescription : declineTask;

  const id = useId();

  const getModalTitle = () => {
    if (taskKey === OutboundTaskTypeKey.PHARMACY_REQUESTED) {
      return 'Close task and cancel prescription';
    }
    return cancelsPrescription ? 'Cancel prescription' : `Decline ${taskKeyToItemLabel[taskKey]}`;
  };

  return (
    <Modal onClose={closeModal}>
      <ModalTitle>{getModalTitle()}</ModalTitle>

      <ModalBody>
        <ExtraInfoTable
          data={extraInfoData}
          minFieldLabelWidth="60px"
          marginTop="0"
          tableMarginBottom={MODAL_INNER_GAP}
          hairlineComponent={<Hairline $marginTop="0" $marginBottom="0" />}
        />

        {cancelsPrescription ? (
          <Typography styledAs="bodyDMSans" id={`decline-reason-${id}`}>
            Tandem will stop processing this prescription and notify the patient of cancellation. Please select a reason
            for cancellation:
          </Typography>
        ) : (
          <Typography styledAs="bodyDMSans" id={`decline-reason-${id}`}>
            Why are you declining {taskKeyToItemLabel[taskKey]}?
          </Typography>
        )}

        <RadioGroup aria-labelledby={`decline-reason-${id}`}>
          {modalDeclineOptions.map((reason) => (
            <RadioOption key={reason.value}>
              <RadioButton
                type="radio"
                id={reason.value}
                name="declineReason"
                value={reason.value}
                checked={declineReason === reason.value}
                onChange={() => setDeclineReason(reason.value as DeclineReason)}
              />
              <RadioLabel as="label" htmlFor={reason.value}>
                {reason.label}
              </RadioLabel>
            </RadioOption>
          ))}
        </RadioGroup>
      </ModalBody>

      <ModalFooter>
        <Button variant="tertiary" onClick={closeModal}>
          {cancelsPrescription ? 'Back' : 'Cancel'}
        </Button>
        <Button
          onClick={() => submitMutation.mutate()}
          disabled={!declineReason || submitMutation.isPending}
          isLoading={submitMutation.isPending}
        >
          {cancelsPrescription ? 'Cancel prescription' : 'Submit'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default DeclineModal;
